import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api';
import { Button, EmptyState, Field, MoneyInput, Table, TextInput } from '../../shell/kit';
import { useToast } from '../../shell/toast';
import {
  createPackage,
  createService,
  fetchPackages,
  fetchServices,
  setPackagePrice,
  setServiceOnSite,
  updatePackage,
  updateService,
  type PackageRow,
  type ServiceRow,
} from './api';
import { money, type PosKey, type PosTranslate } from './i18n';

/**
 * Справочник услуг и пакетов (ТЗ §4.1, М6).
 *
 * Услуги заводил только сид разработчика, пакеты не читал никто. Здесь сеть
 * заводит и архивирует услуги и пакеты, а объект управляет своей стороной:
 * продаёт ли услугу у себя и по какой цене продаёт пакет. Кнопки уровня сети
 * видны только роли с сетевой привязкой — сервер проверяет то же самое.
 */

function Panel({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="p-4">
        {hint ? <p className="mb-3 text-xs text-ink-muted">{hint}</p> : null}
        {children}
      </div>
    </section>
  );
}

function useGuard(onDone: () => Promise<void>) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, okText?: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      await onDone();
      if (okText) toast.ok(okText);
      return true;
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

export function ServicesPanel({
  siteId,
  t,
  network,
  canManage,
  onChanged,
}: {
  siteId: string;
  t: PosTranslate;
  network: boolean;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<readonly ServiceRow[] | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('wash');
  const [duration, setDuration] = useState('');

  const load = useCallback(async () => {
    setRows(await fetchServices(siteId));
  }, [siteId]);
  const { busy, run } = useGuard(async () => {
    await load();
    onChanged();
  });

  useEffect(() => {
    void load().catch(() => setRows([]));
  }, [load]);

  return (
    <Panel title={t('services.title')} hint={t('services.hint')}>
      {rows === null ? null : rows.length === 0 ? (
        <EmptyState title={t('common.empty')} />
      ) : (
        <Table>
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">{t('services.name')}</th>
              <th className="pb-2 font-medium">{t('catalog.code')}</th>
              <th className="pb-2 font-medium">{t('services.category')}</th>
              <th className="pb-2 text-right font-medium">{t('services.duration')}</th>
              <th className="pb-2 font-medium">{t('services.status')}</th>
              <th className="pb-2 font-medium">{t('services.onSite')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-line">
                <td className="py-2 text-ink">{row.name}</td>
                <td className="py-2 text-ink-muted">{row.code}</td>
                <td className="py-2 text-ink-muted">{row.category}</td>
                <td className="py-2 text-right tabular-nums">{row.durationMin}</td>
                <td className="py-2">
                  <span className={row.status === 'archived' ? 'text-ink-faint' : 'text-ink'}>
                    {row.status === 'archived' ? t('services.archived') : t('services.active')}
                  </span>
                  {network ? (
                    <button
                      type="button"
                      disabled={busy}
                      className="ml-2 text-xs text-brand-700 hover:underline disabled:text-ink-faint"
                      onClick={() =>
                        void run(() =>
                          updateService(row.id, { status: row.status === 'archived' ? 'active' : 'archived' }),
                        )
                      }
                    >
                      {row.status === 'archived' ? t('services.restore') : t('services.archive')}
                    </button>
                  ) : null}
                </td>
                <td className="py-2">
                  <span className="text-ink-muted">
                    {row.enabledOnSite === false ? t('services.disabled') : t('services.enabled')}
                  </span>
                  {canManage && row.status === 'active' ? (
                    <button
                      type="button"
                      disabled={busy}
                      className="ml-2 text-xs text-brand-700 hover:underline disabled:text-ink-faint"
                      onClick={() => void run(() => setServiceOnSite(row.id, siteId, row.enabledOnSite === false))}
                    >
                      {row.enabledOnSite === false ? t('services.enable') : t('services.disable')}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {network ? (
        <div className="mt-4 grid gap-3 md:grid-cols-5">
          <Field label={t('catalog.code')} required>
            <TextInput value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} />
          </Field>
          <Field label={t('services.name')} required>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('services.category')}>
            <TextInput value={category} onChange={(e) => setCategory(e.target.value)} />
          </Field>
          <Field label={t('services.duration')}>
            <TextInput inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} />
          </Field>
          <div className="flex items-end">
            <Button
              tone="primary"
              disabled={busy || !code.trim() || !name.trim()}
              onClick={() =>
                void run(
                  () =>
                    createService({
                      code: code.trim(),
                      name: name.trim(),
                      ...(category.trim() ? { category: category.trim() } : {}),
                      ...(duration.trim() ? { durationMin: Number(duration) || 0 } : {}),
                    }),
                  t('services.created'),
                ).then((ok) => {
                  if (ok) {
                    setCode('');
                    setName('');
                    setDuration('');
                  }
                })
              }
            >
              {t('services.create')}
            </Button>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

export function PackagesPanel({
  siteId,
  t,
  network,
  canManage,
}: {
  siteId: string;
  t: PosTranslate;
  network: boolean;
  canManage: boolean;
}) {
  const [rows, setRows] = useState<readonly PackageRow[] | null>(null);
  const [services, setServices] = useState<readonly ServiceRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, number | null>>({});
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState<number | null>(null);
  const [picked, setPicked] = useState<readonly string[]>([]);

  const load = useCallback(async () => {
    setRows(await fetchPackages(siteId));
    setServices((await fetchServices(siteId)).filter((s) => s.status === 'active'));
  }, [siteId]);
  const { busy, run } = useGuard(load);

  useEffect(() => {
    void load().catch(() => setRows([]));
  }, [load]);

  return (
    <Panel title={t('packages.title')} hint={t('packages.hint')}>
      {rows === null ? null : rows.length === 0 ? (
        <EmptyState title={t('packages.empty')} />
      ) : (
        <Table>
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">{t('services.name')}</th>
              <th className="pb-2 font-medium">{t('packages.items')}</th>
              <th className="pb-2 text-right font-medium">{t('packages.networkPrice')}</th>
              <th className="pb-2 text-right font-medium">{t('packages.sitePrice')}</th>
              <th className="pb-2 font-medium">{t('catalog.newPrice')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-line">
                <td className="py-2 text-ink">
                  {row.name}
                  <div className="text-xs text-ink-muted">
                    {row.code} · {row.status === 'archived' ? t('services.archived') : t('services.active')}
                    {network ? (
                      <button
                        type="button"
                        disabled={busy}
                        className="ml-2 text-brand-700 hover:underline disabled:text-ink-faint"
                        onClick={() =>
                          void run(() =>
                            updatePackage(row.id, { status: row.status === 'archived' ? 'active' : 'archived' }),
                          )
                        }
                      >
                        {row.status === 'archived' ? t('services.restore') : t('services.archive')}
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="py-2 text-ink-muted">
                  {row.items.map((item) => `${item.serviceName}${item.qty > 1 ? ` × ${item.qty}` : ''}`).join(', ')}
                </td>
                <td className="py-2 text-right tabular-nums">{money(row.networkPriceMinor)}</td>
                <td className="py-2 text-right tabular-nums">
                  {row.price ? money(row.price.priceMinor) : '—'}
                  {row.price ? (
                    <div className="text-xs text-ink-muted">{t(`catalog.scope.${row.price.scope}` as PosKey)}</div>
                  ) : null}
                </td>
                <td className="py-2">
                  {canManage ? (
                    <div className="flex items-center gap-2">
                      <div className="w-28">
                        <MoneyInput
                          valueMinor={drafts[row.id] ?? null}
                          onChangeMinor={(value) => setDrafts((prev) => ({ ...prev, [row.id]: value }))}
                        />
                      </div>
                      <Button
                        size="sm"
                        disabled={busy || !drafts[row.id]}
                        onClick={() =>
                          void run(() => setPackagePrice(row.id, siteId, drafts[row.id] ?? 0)).then(() =>
                            setDrafts((prev) => ({ ...prev, [row.id]: null })),
                          )
                        }
                      >
                        {t('packages.setSitePrice')}
                      </Button>
                      {network ? (
                        <Button
                          size="sm"
                          disabled={busy || !drafts[row.id]}
                          onClick={() =>
                            void run(() => setPackagePrice(row.id, null, drafts[row.id] ?? 0)).then(() =>
                              setDrafts((prev) => ({ ...prev, [row.id]: null })),
                            )
                          }
                        >
                          {t('packages.setNetworkPrice')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {network ? (
        <div className="mt-4 flex flex-col gap-3">
          <div className="grid gap-3 md:grid-cols-4">
            <Field label={t('catalog.code')} required>
              <TextInput value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} />
            </Field>
            <Field label={t('services.name')} required>
              <TextInput value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t('packages.networkPrice')} required>
              <MoneyInput valueMinor={price} onChangeMinor={setPrice} />
            </Field>
            <div className="flex items-end">
              <Button
                tone="primary"
                disabled={busy || !code.trim() || !name.trim() || price === null || picked.length === 0}
                onClick={() =>
                  void run(
                    () =>
                      createPackage({
                        code: code.trim(),
                        name: name.trim(),
                        priceMinor: price ?? 0,
                        items: picked.map((serviceId) => ({ serviceId })),
                      }),
                    t('packages.created'),
                  ).then((ok) => {
                    if (ok) {
                      setCode('');
                      setName('');
                      setPrice(null);
                      setPicked([]);
                    }
                  })
                }
              >
                {t('packages.create')}
              </Button>
            </div>
          </div>
          <fieldset>
            <legend className="text-xs text-ink-muted">{t('packages.pickServices')}</legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {services.map((service) => (
                <label key={service.id} className="flex items-center gap-1 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={picked.includes(service.id)}
                    onChange={(e) =>
                      setPicked((prev) =>
                        e.target.checked ? [...prev, service.id] : prev.filter((id) => id !== service.id),
                      )
                    }
                  />
                  {service.name}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      ) : null}
    </Panel>
  );
}
